// Pattern: docs/design-patterns/frontend/state-management — server state in TanStack Query

import { useQuery } from '@tanstack/react-query';

import * as frameworksApi from '~/services/api/frameworks';

import type { FrameworkDetail, FrameworkManifest } from '~/types';

import { useAuth } from './useAuth';

export const frameworkKeys = {
  all: ['frameworks'] as const,
  manifest: () => [...frameworkKeys.all, 'manifest'] as const,
  detail: (id: string) => [...frameworkKeys.all, 'detail', id] as const,
};

const THIRTY_MINUTES = 30 * 60 * 1000;

export function useFrameworkDetail(frameworkId: string | undefined): {
  error: unknown;
  framework: FrameworkDetail | null;
  isLoading: boolean;
} {
  const { loading, user } = useAuth();

  const {
    data: framework,
    error,
    isLoading,
  } = useQuery({
    queryKey: frameworkKeys.detail(frameworkId ?? ''),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by `enabled: !!frameworkId`
    queryFn: () => frameworksApi.getFramework(frameworkId!),
    enabled: !loading && !!user && !!frameworkId,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    framework: framework || null,
    isLoading,
    error,
  };
}

export function useFrameworkManifest(): {
  error: unknown;
  isLoading: boolean;
  manifest: FrameworkManifest | null;
} {
  const { loading, user } = useAuth();

  const {
    data: manifest,
    error,
    isLoading,
  } = useQuery({
    queryKey: frameworkKeys.manifest(),
    queryFn: frameworksApi.getFrameworkManifest,
    enabled: !loading && !!user,
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return {
    manifest: manifest || null,
    isLoading,
    error,
  };
}
